import { useCallback } from "react";

import { ProductKeys } from "@atlassiansox/asc-stargate";
import {
    Journeys,
    Options,
    TargetType,
    useCrossFlow
} from "@atlassiansox/cross-flow-support";

import { useSitesContext } from "@/components/sites-provider";

import { CrossFlowApiParams } from "../../types";
import {
    WAC_GENERIC_TOUCHPOINT_NAME,
    getRolloutFlagValue,
    redirectToDefaultUrl,
    useFireEvent
} from "../../utils";

export const useOpenCrossFlow = ({
    productKey,
    redirectUrl,
    edition,
    componentType = "button",
    interactionName = "tryItFreeButton",
    screenName
}: CrossFlowApiParams) => {
    const {
        fireCrossFlowInteractedUIEvent,
        fireOperationalEvent,
        fireTrackEvent
    } = useFireEvent({
        productKey,
        redirectUrl,
        edition,
        componentType,
        interactionName,
        screenName
    });

    const crossFlow = useCrossFlow();

    const fireInteractionEvent = useCallback(() => {
        if (screenName && productKey) {
            fireCrossFlowInteractedUIEvent(
                {
                    action: "clicked",
                    actionSubject: componentType,
                    actionSubjectId: interactionName
                },
                {
                    source: screenName,
                    targetProductId: productKey
                }
            );
        } else {
            fireTrackEvent({
                action: "clicked",
                actionSubject: componentType,
                actionSubjectId: interactionName
            });
        }
    }, [
        componentType,
        fireCrossFlowInteractedUIEvent,
        fireTrackEvent,
        interactionName,
        productKey,
        screenName
    ]);
    const fireRedirectSuccessEvent = useCallback(() => {
        fireOperationalEvent({
            action: "successful",
            actionSubject: "crossFlowFallbackRedirectAttempt",
            eventType: "operational"
        });
    }, [fireOperationalEvent]);
    const fireRedirectFailedEvent = useCallback(
        (errorMessage: string) =>
            fireOperationalEvent({
                action: "failed",
                actionSubject: "crossFlowFallbackRedirectAttempt",
                attributes: {
                    errorMessage
                }
            }),
        [fireOperationalEvent]
    );

    const { sites } = useSitesContext();

    // * For Streamline V2 experiment, only admin with one site is eligible. If not eligible, fallback to default CFFE behavior
    const isStreamlineWacV2Eligible =
        sites?.length === 1 &&
        sites.some(
            (site) =>
                site.adminAccess &&
                !site.products.includes(ProductKeys.JIRA_SERVICE_DESK)
        );

    const openCrossFlow = useCallback(
        async (event?: React.MouseEvent, payload: CrossFlowApiParams = {}) => {
            if (event) {
                event.preventDefault();
            }
            const targetProduct = payload.productKey || productKey;
            const url = payload.redirectUrl || redirectUrl || "";
            const targetEdition = payload.edition || edition;
            try {
                fireInteractionEvent();
                if (
                    !targetProduct ||
                    !crossFlow.isEnabled ||
                    getRolloutFlagValue(targetProduct, true) !== "variation"
                ) {
                    redirectToDefaultUrl(
                        url,
                        fireRedirectSuccessEvent,
                        fireRedirectFailedEvent
                    );
                    return;
                }
                fireOperationalEvent({
                    action: "successful",
                    actionSubject: "crossFlowRequest"
                });

                let extensions: Options["extensions"];
                if (targetEdition) {
                    extensions = {
                        edition: targetEdition
                    };
                }
                const experimentalOptions: Options["experimentalOptions"] = {
                    isStreamlineWacV2Eligible
                };

                await crossFlow.api.open({
                    journey: Journeys.GET_STARTED,
                    targetProduct: targetProduct as TargetType,
                    sourceComponent: screenName || WAC_GENERIC_TOUCHPOINT_NAME,
                    sourceContext: interactionName,
                    extensions,
                    experimentalOptions
                });
            } catch (error) {
                const errorData = error as Error;
                fireOperationalEvent({
                    action: "failed",
                    actionSubject: "crossFlowRequest",

                    attributes: {
                        errorMessage: errorData?.message,
                        errorStack: errorData?.stack,
                        url
                    }
                });
                redirectToDefaultUrl(
                    url,
                    fireRedirectSuccessEvent,
                    fireRedirectFailedEvent
                );
            }
        },
        [
            productKey,
            redirectUrl,
            edition,
            fireInteractionEvent,
            crossFlow,
            fireOperationalEvent,
            isStreamlineWacV2Eligible,
            screenName,
            interactionName,
            fireRedirectSuccessEvent,
            fireRedirectFailedEvent
        ]
    );

    return openCrossFlow;
};
